import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, mediaUrl } from "../lib/api";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import WhatsAppButton from "../components/site/WhatsAppButton";
import { ArrowLeft, ArrowRight, FileDown, Loader2 } from "lucide-react";

export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setProduct(null); setNotFound(false);
    (async () => {
      try {
        const { data } = await api.get(`/products/${slug}`);
        setProduct(data);
        const rel = await api.get("/products", { params: { category: data.category } });
        setRelated(rel.data.filter((p) => p.slug !== slug).slice(0, 3));
      } catch {
        setNotFound(true);
      }
    })();
  }, [slug]);

  const goEnquire = () => navigate("/", { state: { scrollTo: "enquiry" } });

  if (notFound) return (
    <div className="grain min-h-screen"><Navbar /><div className="min-h-screen grid place-items-center">
      <div className="text-center"><div className="font-head font-black uppercase text-4xl text-white mb-4">Product not found</div>
      <Link to="/" className="font-mono text-sm text-signal">← Back to catalog</Link></div></div></div>
  );

  if (!product) return (
    <div className="min-h-screen grid place-items-center bg-ink"><Loader2 className="w-8 h-8 animate-spin text-signal" /></div>
  );

  return (
    <div className="grain">
      <Navbar />
      <main className="pt-[72px]">
        <section className="tech-grid border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
            <button onClick={() => navigate(-1)} data-testid="pp-back"
              className="font-mono text-[11px] uppercase tracking-widest text-slate-400 hover:text-signal flex items-center gap-2 mb-10">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
              <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
                className="relative bg-card border border-white/10 ticks overflow-hidden">
                <img src={mediaUrl(product.image_url)} alt={product.name} className="w-full aspect-square object-cover" data-testid="pp-image" />
                <span className="absolute top-4 left-4 font-mono text-[11px] uppercase tracking-widest bg-signal text-ink px-3 py-1.5">{product.brand}</span>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}>
                <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-signal mb-4">{product.category}</div>
                <h1 className="font-head font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-white mb-6" data-testid="pp-name">{product.name}</h1>
                <p className="text-slate-400 text-lg leading-relaxed mb-8">{product.description}</p>

                <div className="border border-white/10">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-signal px-5 py-3 border-b border-white/10 bg-card">Specifications</div>
                  <dl className="divide-y divide-white/5">
                    {Object.entries(product.specs || {}).map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-4 px-5 py-3">
                        <dt className="font-mono text-xs text-slate-500 uppercase">{k}</dt>
                        <dd className="font-mono text-sm text-white text-right">{v}</dd>
                      </div>
                    ))}
                    {Object.keys(product.specs || {}).length === 0 && (
                      <div className="px-5 py-4 font-mono text-xs text-slate-500">Specifications available on request.</div>
                    )}
                  </dl>
                </div>

                <div className="flex flex-wrap gap-4 mt-8">
                  <button onClick={goEnquire} data-testid="pp-enquire"
                    className="group font-head font-bold uppercase tracking-wider px-7 py-4 bg-signal text-ink hover:bg-signal-hover transition-colors flex items-center gap-2">
                    Request Quote <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                  {product.datasheet_url && (
                    <a href={mediaUrl(product.datasheet_url)} target="_blank" rel="noopener noreferrer" data-testid="pp-datasheet"
                      className="font-head font-bold uppercase tracking-wider px-7 py-4 border border-white/20 text-white hover:border-signal hover:text-signal transition-colors flex items-center gap-2">
                      <FileDown className="w-5 h-5" /> Download Datasheet
                    </a>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {related.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="flex items-center gap-3 mb-10">
              <span className="w-10 h-px bg-signal" />
              <span className="font-mono text-[11px] tracking-[0.35em] uppercase text-signal">Related Components</span>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link key={p.id} to={`/product/${p.slug}`} data-testid={`related-${p.slug}`}
                  className="group bg-card border border-white/10 hover:border-signal/50 transition-colors overflow-hidden">
                  <div className="aspect-[4/3] overflow-hidden bg-ink">
                    <img src={mediaUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
                  </div>
                  <div className="p-6">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">{p.brand}</div>
                    <h3 className="font-head font-bold uppercase text-xl text-white leading-tight">{p.name}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}
